import { useMemo, useState } from 'react';

import {
  Box,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  Line,
  Stack,
  TimeXAxis,
  YAxis,
} from '@cast/design-system';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { FailedToLoad } from '@cast/shared-messages-ui';
import {
  buildAnalyzePrompt,
  useAnalyzeWithOpsPilot,
} from '@cast/features-organization-shared-opspilot';
import {
  ReliabilitySegmentDot,
  buildPairedGapEdges,
  ReliabilityDatapoint,
  buildLatencyTickFormatter,
  formatLatency,
  formatLatencyWithUnit,
} from '@cast/split-me/features/cluster/_components/reliability';

import { useWorkloadReliabilityData } from '@cast/split-me/features/cluster/workload-optimization';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';
import { isRangeSupported } from '../../_utils/isRangeSupported';
import { DateRangeUnsupportedBanner } from '../_components/DateRangeUnsupportedBanner';
import { ReliabilityMetricsSummary } from './_components/ReliabilityMetricsSummary';

export const ReliabilityLatencyChart = () => {
  const { timeZone, getRange, preset, key, from, to } =
    useInventoryWorkloadsTimeRangeSelection();
  const timeRangeSelection = {
    getRange,
    preset,
    timeZone,
    key,
  };
  const { cluster } = useCluster();
  const {
    data,
    chartData,
    latencyMetrics,
    isLoading,
    isFetching,
    showNoData,
    error,
    refetch,
  } = useWorkloadReliabilityData({ timeRangeSelection });

  const hasError = !!error;
  const rangeSupported = isRangeSupported(from, to);

  const [visibleMetrics, setVisibleMetrics] = useState<Record<string, boolean>>(
    () => Object.fromEntries(latencyMetrics.map((m) => [m.key, true]))
  );

  const toggleMetric = (key: string): void => {
    setVisibleMetrics((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const visibleMetricsList = latencyMetrics.filter(
    (m) => visibleMetrics[m.key]
  );

  const visibleOrAllMetricKeys = useMemo(() => {
    const visibleKeys = visibleMetricsList.map((m) => m.key);
    const allKeys = latencyMetrics.map((m) => m.key);
    return visibleKeys.length ? visibleKeys : allKeys;
  }, [visibleMetricsList, latencyMetrics]);

  const pairedGapEdges = useMemo(
    () =>
      Object.fromEntries(
        latencyMetrics.map((m) => [
          m.key,
          buildPairedGapEdges(chartData, m.key),
        ])
      ),
    [chartData, latencyMetrics]
  );

  const latencyTickFormatter = useMemo(
    () => buildLatencyTickFormatter(chartData, visibleOrAllMetricKeys),
    [chartData, visibleOrAllMetricKeys]
  );

  const analyzeWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: buildAnalyzePrompt({
      context: 'Latency reliability metrics',
      scopeName: cluster.name,
      getDefaultRange: getRange,
    }),
  });

  if (!rangeSupported) {
    return (
      <WidgetLayout
        title="Reliability latency"
        testId="workload-reliability-latency-chart"
      >
        <DateRangeUnsupportedBanner testId="workload-reliability-latency-chart-unsupported-range" />
      </WidgetLayout>
    );
  }

  return (
    <WidgetLayout
      title="Reliability latency"
      testId="workload-reliability-latency-chart"
    >
      {hasError && !isFetching ? (
        <FailedToLoad
          title="Failed to load reliability latency"
          refresh={refetch}
        />
      ) : (
        <Stack gap={12} height="100%">
          <ReliabilityMetricsSummary
            metrics={latencyMetrics}
            data={data}
            visibleMetrics={visibleMetrics}
            toggleMetric={toggleMetric}
            testId="workload-reliability-latency-chart"
            isLoading={isLoading || (hasError && isFetching)}
            formatValue={(summary) => {
              const formatted = formatLatency(summary, 2);
              return {
                value: formatted?.value,
                unit:
                  summary !== undefined ? formatted?.unit ?? 'ms' : undefined,
              };
            }}
          />
          <Box
            position="relative"
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            flex={1}
            minHeight={0}
          >
            <ChartContainer
              data={chartData}
              isLoading={isLoading || (hasError && isFetching)}
              hasNoData={showNoData}
              syncId="workload-reliability"
              analyzeWithOpsPilot={analyzeWithOpsPilot}
            >
              <ChartGrid hideVertical />
              {visibleMetricsList.map((metric) => (
                <Line<ReliabilityDatapoint>
                  key={metric.key}
                  dataKey={metric.key}
                  stroke={metric.stroke}
                  connectNulls={false}
                  isAnimationActive={false}
                  tooltipConfig={{
                    displayName: metric.label,
                  }}
                  dot={
                    <ReliabilitySegmentDot
                      activeDotIndices={pairedGapEdges[metric.key]}
                    />
                  }
                />
              ))}
              <TimeXAxis<ReliabilityDatapoint>
                unixDataKey="chartTimestampUnix"
                timezone={timeZone}
              />
              <YAxis<ReliabilityDatapoint>
                domain={[
                  'dataMin',
                  (dataMax: number) => Math.max(dataMax * 1.2, 1),
                ]}
                allowDecimals
                tickFormatter={latencyTickFormatter}
              />
              <ChartTooltip<ReliabilityDatapoint>
                header={(headerProps) => (
                  <ChartTooltipTimestampHeader
                    {...headerProps}
                    timezone={timeZone}
                  />
                )}
                formatValue={(value) => formatLatencyWithUnit(value, 2)}
              />
            </ChartContainer>
          </Box>
        </Stack>
      )}
    </WidgetLayout>
  );
};
